/*****
 * 
 * Refatorado em 05/07/2026
 * 
 */

import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CargoApiService } from 'app/api/cargoApi.service';
import { CienciaApiService } from 'app/api/api';
import { AlertModalService } from 'app/shared/alert-modal.service';
import { Ciencia, Cargo, NewPerfil } from 'app/model/models';
import { SubSink } from 'subsink';
import { forkJoin, of } from 'rxjs';
import { catchError, take, map } from 'rxjs/operators';

@Component({
  selector: 'app-cargos-filtro',
  templateUrl: './cargos-filtro.component.html',
  styleUrls: ['./cargos-filtro.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush // <--- Protege o componente pai de re-renderizações bobas
})
export class CargosFiltroComponent implements OnInit, OnDestroy {

  cargo: Cargo;
  ciencias: Ciencia[];
  resultadoFiltroServico: NewPerfil[];

  idCiencia: number;
  
  resultadoFiltroAxiologia: any;
  resultadoFiltroComportamento: any;
  resultadoFiltroMotivadores: any;
  
  printWait = false;
  reverse = true;

  reloadPerfisAxio = false;
  reloadPerfisCompo = false;
  reloadPerfisMoti = false;
  errorPerfisAxio = false;
  errorPerfisCompo = false;
  errorPerfisMoti = false;
  loading = false;
  
  @Input() idCargo: number;
  @Input() tipoCargo: string;

  private updatePerfis: any;

  get saveUpdate() {
    return this.updatePerfis;
  }

  @Input()
  set saveUpdate(saveUpdate) {
    this.updatePerfis = saveUpdate;
    if (saveUpdate) {
      this.savePerfil();
    }
  }

  private subs = new SubSink();

  constructor(
    private cargosService: CargoApiService,
    private cienciasService: CienciaApiService,
    private alertService: AlertModalService,
    private router: Router,
    private cdr: ChangeDetectorRef // <--- Gerencia as atualizações de visualização controladas
  ) { }

  ngOnInit() {
    this.getCiencia();
  }

  getCiencia(): void {
    this.subs.sink = this.cienciasService.v1CienciaGet()
      .subscribe(ciencias => {
        this.ciencias = ciencias;
        this.cdr.markForCheck();
      });
  }

  recebeResultadoFiltroAxiologia(resultadoFiltroAxiologia) {
    this.resultadoFiltroAxiologia = resultadoFiltroAxiologia;
  }

  recebeResultadoFiltroComportamento(resultadoFiltroComportamento) {
    this.resultadoFiltroComportamento = resultadoFiltroComportamento;
  }

  recebeResultadoFiltroMotivadores(resultadoFiltroMotivadores) {
    this.resultadoFiltroMotivadores = resultadoFiltroMotivadores;
  }

  savePerfil() {
    if (!this.resultadoFiltroAxiologia && !this.resultadoFiltroComportamento && !this.resultadoFiltroMotivadores) {
      this.alertService.showAlertWarning('Nenhum perfil foi selecionado!');
      return;
    }

    this.loading = true;
    this.errorPerfisAxio = false;
    this.errorPerfisCompo = false;
    this.errorPerfisMoti = false;
    this.reloadPerfisAxio = false;
    this.reloadPerfisCompo = false;
    this.reloadPerfisMoti = false;
    this.cdr.markForCheck();

    // Criamos as tarefas HTTP baseadas nas condições de forma limpa, sem dispará-las ainda
    const requests = {
      axiologia: this.prepareUpdateObservable(this.resultadoFiltroAxiologia, 1),
      comportamento: this.prepareUpdateObservable(this.resultadoFiltroComportamento, 2),
      motivadores: this.prepareUpdateObservable(this.resultadoFiltroMotivadores, 3)
    };

    // O forkJoin dispara todas em paralelo e espera a resposta coletiva de todas de uma vez só!
    this.subs.sink = forkJoin(requests).pipe(take(1)).subscribe({
      next: (res: any) => {
        this.loading = false;

        // Processa os estados de sucesso/erro baseados nas respostas interceptadas
        this.reloadPerfisAxio = res.axiologia?.success && res.axiologia?.updated;
        this.errorPerfisAxio = res.axiologia?.error;

        this.reloadPerfisCompo = res.comportamento?.success && res.comportamento?.updated;
        this.errorPerfisCompo = res.comportamento?.error;

        this.reloadPerfisMoti = res.motivadores?.success && res.motivadores?.updated;
        this.errorPerfisMoti = res.motivadores?.error;

        this.processFinalFeedback();
      },
      error: () => {
        this.loading = false;
        this.alertService.showAlertDanger('Erro crítico ao processar requisições.');
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Prepara o Observable HTTP correto de acordo com o estado do filtro enviado
   */
  private prepareUpdateObservable(filtro: any[], idCiencia: number) {
    if (!filtro) {
      return of({ success: true, updated: false, error: false });
    }

    if (filtro.length === 0) {
      const fallbackPayload = [{ "minimo": 0, "ideal": 0, "maximo": 0, "fator": null }];
      return this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(fallbackPayload, this.idCargo, idCiencia, 'response')
        .pipe(
          take(1),
          map(res => ({ success: true, updated: true, error: false })), // <--- Corrigido aqui
          catchError(() => of({ success: false, updated: false, error: true }))
        );
    }

    // Otimização: Substituído o .find() lento por uma checagem direta do primeiro registro do array
    const primeiroFator = filtro[0];
    if (primeiroFator && primeiroFator.fator) {
      return this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(filtro, this.idCargo, idCiencia, 'response')
        .pipe(
          take(1),
          map(res => ({ success: true, updated: true, error: false })), // <--- Corrigido aqui
          catchError(() => of({ success: false, updated: false, error: true }))
        );
    }

    return of({ success: true, updated: false, error: false });
  }
  
  /**
   * Centraliza os feedbacks visuais de encerramento do processo de salvamento
   */
  private processFinalFeedback() {
    if (this.errorPerfisAxio || this.errorPerfisCompo || this.errorPerfisMoti) {
      this.alertService.showAlertDanger('Erro ao cadastrar Perfis. Por favor, verifique os filtros e tente novamente.');
    } 

    const houveAlgumSucesso = this.reloadPerfisAxio || this.reloadPerfisCompo || this.reloadPerfisMoti;

    if (houveAlgumSucesso) {
      if (!this.saveUpdate) {
        this.alertService.showAlertSuccess('Perfis cadastrados com sucesso!');
      } else {
        this.goBack();
      }
      this.goTop();
    } else {
      if (!this.saveUpdate) {
        this.alertService.showAlertWarning('Não houve alteração nos Perfis Comportamentais!');
      } else {
        this.goBack();
      }
    }
    this.cdr.markForCheck();
  }

  customComparator(itemA, itemB) {
    const hasNumber = /\d/;
    if (hasNumber.test(itemA)) {
      return itemA - itemB;
    } else {
      return itemA.localeCompare(itemB);
    }
  }

  goBack(): void {
    let url = 'cargos/';
    this.tipoCargo && this.tipoCargo === 'Modelo' ? url = 'cargos-modelo/' : url = 'cargos/'
    this.router.navigateByUrl('/SkipPage', { skipLocationChange: true }).then(() =>
      this.router.navigate([url + this.idCargo])
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private goTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }
}

/*
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CargoApiService } from 'app/api/cargoApi.service';
import { CienciaApiService } from 'app/api/api';
import { AlertModalService } from 'app/shared/alert-modal.service';
import { 
  Ciencia, 
  Cargo,
  NewPerfil
} from 'app/model/models';
import { SubSink } from 'subsink';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-cargos-filtro',
  templateUrl: './cargos-filtro.component.html',
  styleUrls: ['./cargos-filtro.component.css'],
})

export class CargosFiltroComponent implements OnInit, OnDestroy {

  cargo: Cargo;
  ciencias: Ciencia[];
  resultadoFiltroServico: NewPerfil[];

  idCiencia: number;
  
  resultadoFiltroAxiologia;
  resultadoFiltroComportamento;
  resultadoFiltroMotivadores;
  
  printWait = false;
  reverse = true;

  reloadPerfisAxio = false;
  reloadPerfisCompo = false;
  reloadPerfisMoti = false;
  errorPerfisAxio = false;
  errorPerfisCompo = false;
  errorPerfisMoti = false;
  checkResponse = [];
  loadUpdate = [];
  loading = false;
  
  @Input() idCargo: number;

  private updatePerfis;

  get saveUpdate() {
    return this.updatePerfis;
  }

  @Input()
  set saveUpdate(saveUpdate) {
    this.updatePerfis = saveUpdate;
    if (saveUpdate) {
      this.savePerfil();
    }
  }

  private subs = new SubSink();

  constructor(
    private cargosService: CargoApiService,
    private cienciasService: CienciaApiService,
    private alertService: AlertModalService,
    private router: Router,
  ) { }

  ngOnInit() {
    this.getCiencia();
  }

  getCiencia(): void {
    this.subs.sink = this.cienciasService.v1CienciaGet()
      .subscribe(
        ciencias => {
          this.ciencias = ciencias;
        });
  }

  
  //  * Recebe o filtro gerado no cargos-search-axiologia.component
   
  recebeResultadoFiltroAxiologia(resultadoFiltroAxiologia) {
    this.resultadoFiltroAxiologia = resultadoFiltroAxiologia;
  }

  
  // * Recebe o filtro gerado no cargos-search-comportamento.component
  
  recebeResultadoFiltroComportamento(resultadoFiltroComportamento) {
    this.resultadoFiltroComportamento = resultadoFiltroComportamento;
  }

  
  // * Recebe o filtro gerado no cargos-search-motivadores.component
  
  recebeResultadoFiltroMotivadores(resultadoFiltroMotivadores) {
    this.resultadoFiltroMotivadores = resultadoFiltroMotivadores;
  }

  savePerfil() {

    this.checkResponse = [];
    this.loadUpdate = [];
    this.loading = true;
    this.reloadPerfisAxio = false;
    this.reloadPerfisCompo = false;
    this.reloadPerfisMoti = false;
    
    const perfilAxiologia = this.resultadoFiltroAxiologia;
    const perfilComportamento = this.resultadoFiltroComportamento;
    const perfilMotivadores = this.resultadoFiltroMotivadores;

    this.getUpdatePerfis(perfilAxiologia, perfilComportamento, perfilMotivadores);
 
  }

  async updatePerfisAxiologia(filtroAxiologia) {

    let arrayCienciaAxiologia: Array<NewPerfil> = [];
    let stop = false;

    if (filtroAxiologia) {

      const idCiencia = 1;

      if (filtroAxiologia?.length === 0) {

        arrayCienciaAxiologia = [{ "minimo": 0, "ideal": 0, "maximo": 0, "fator": null }];

        this.subs.sink = this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(arrayCienciaAxiologia, this.idCargo, idCiencia, 'response')
          .subscribe(
            success => { 
              this.reloadPerfisAxio = true; 
              this.loadUpdate.push(1);
              this.setUpdatePerfis(this.loadUpdate);
            }, 
            error => { 
              this.errorPerfisAxio = true;
              this.loadUpdate.push(1);
              this.setUpdatePerfis(this.loadUpdate);
            }
          );

      } else { 

         filtroAxiologia.find(fator => {  
          // o array padrão tem o campo 'nome' no lugar do campo 'fator' que está sendo verificado no if (fator.fator) 
          // então, se o array recebido tiver 'fator' ao inves de 'nome' estará vindo do formulário e ai deverá ser atualizado.
   
            if (fator.fator && !stop) {   
            this.subs.sink = this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(filtroAxiologia, this.idCargo, idCiencia, 'response').pipe(take(1))
              .subscribe(
                success => {
                  this.reloadPerfisAxio = true;
                  this.loadUpdate.push(1);
                  this.setUpdatePerfis(this.loadUpdate);
                }, 
                error => { 
                  this.errorPerfisAxio = true;
                  this.reloadPerfisAxio = false;
                  this.loadUpdate.push(1);
                  this.setUpdatePerfis(this.loadUpdate);
                  // stop = true;
                }
                );
            } else if (!fator.fator && !stop) {
                this.reloadPerfisAxio = false;
                this.loadUpdate.push(1);
                this.setUpdatePerfis(this.loadUpdate);
            }
                  
            stop = true;

          }); 
          
      }

    } else { 
      this.reloadPerfisAxio = false;
      this.loadUpdate.push(1);
      this.setUpdatePerfis(this.loadUpdate);
    }

  }


  async updatePerfisComportamento(filtroComportamento) {

    let arrayCienciaComportamento: Array<NewPerfil> = [];
    let stop = false;

    if (filtroComportamento) {

      const idCiencia = 2;

      if (filtroComportamento?.length === 0) {

        arrayCienciaComportamento = [{ "minimo": 0, "ideal": 0, "maximo": 0, "fator": null }];

        this.subs.sink = this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(arrayCienciaComportamento, this.idCargo, idCiencia, 'response')
          .subscribe(
            success => { 
              this.reloadPerfisCompo = true; 
              this.loadUpdate.push(2);
              this.setUpdatePerfis(this.loadUpdate);
            }, 
            error => { 
              this.errorPerfisCompo = true;
              this.loadUpdate.push(2);
              this.setUpdatePerfis(this.loadUpdate);
            }
          );

      } else { 

        filtroComportamento.find(fator => {  
          // o array padrão tem o campo 'nome' no lugar do campo 'fator' que está sendo verificado no if (fator.fator) 
          // então, se o array recebido tiver 'fator' ao inves de 'nome' estará vindo do formulário e ai deverá ser atualizado.

          if (fator.fator && !stop) {
              
              this.subs.sink = this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(filtroComportamento, this.idCargo, idCiencia, 'response').pipe(take(1))
              .subscribe(
                  success => {
                    this.reloadPerfisCompo = true;
                    this.loadUpdate.push(2);
                    this.setUpdatePerfis(this.loadUpdate);
                  }, 
                  error => { 
                    console.error(error);
                    this.errorPerfisCompo = true;
                    this.loadUpdate.push(2);
                    this.setUpdatePerfis(this.loadUpdate);
                    // stop = true;
                  }
                );
          } else if (!fator.fator && !stop){
              this.reloadPerfisCompo = false;
              this.loadUpdate.push(2);
              this.setUpdatePerfis(this.loadUpdate);
          }
                  
          stop = true;

        });
        
      }

    } else { 
      this.reloadPerfisCompo = false; 
      this.loadUpdate.push(2)
      this.setUpdatePerfis(this.loadUpdate);
    }

  }


  async updatePerfisMotivadores(filtroMotivadores) {

    let arrayCienciaMotivadores: Array<NewPerfil> = [];
    let stop = false;

    if (filtroMotivadores) {

      const idCiencia = 3;

      if (filtroMotivadores?.length === 0) {

        arrayCienciaMotivadores = [{ "minimo": 0, "ideal": 0, "maximo": 0, "fator": null }];

        this.subs.sink = this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(arrayCienciaMotivadores, this.idCargo, idCiencia, 'response')
          .subscribe(
              success => {
                this.reloadPerfisMoti = true;
                this.loadUpdate.push(3);
                this.setUpdatePerfis(this.loadUpdate);
              }, 
              error => {
                this.errorPerfisMoti = true;
                this.loadUpdate.push(3);
                this.setUpdatePerfis(this.loadUpdate);
              }
          );

      } else { 
    
       filtroMotivadores.find(fator => {  
        // o array padrão tem o campo 'nome' no lugar do campo 'fator' que está sendo verificado no if (fator.fator) 
        // então, se o array recebido tiver 'fator' ao inves de 'nome' estará vindo do formulário e ai deverá ser atualizado.
          
           if (fator.fator && !stop) {
            
              this.subs.sink = this.cargosService.v1CargoCargoIdCienciaCienciaIdPerfisPut(filtroMotivadores, this.idCargo, idCiencia, 'response').pipe(take(1))
              .subscribe(
                success => {
                  this.reloadPerfisMoti = true;
                  this.loadUpdate.push(3);
                  this.setUpdatePerfis(this.loadUpdate);
                }, 
                error => { 
                  this.errorPerfisMoti = true;
                  this.reloadPerfisMoti = false;
                  this.loadUpdate.push(3);
                  this.setUpdatePerfis(this.loadUpdate);
                }
                );  
                
                
              } else if (!fator.fator && !stop) {
                  this.reloadPerfisMoti = false;
                  this.loadUpdate.push(3);
                  this.setUpdatePerfis(this.loadUpdate);
              }

              stop = true;
          
        });


      }

    } else { 
      this.reloadPerfisMoti = false; 
      this.loadUpdate.push(3);
      this.setUpdatePerfis(this.loadUpdate);
    }

  }

  async getUpdatePerfis(filtroAxiologia, filtroComportamento, filtroMotivadores) {

    if (filtroAxiologia) {
      await this.updatePerfisAxiologia(filtroAxiologia);
    } else { 
      this.reloadPerfisAxio = false; 
      this.loadUpdate.push(1);
      this.setUpdatePerfis(this.loadUpdate);
    }

    if (filtroComportamento) {
      await this.updatePerfisComportamento(filtroComportamento);
    } else { 
      this.reloadPerfisCompo = false; 
      this.loadUpdate.push(2);
      this.setUpdatePerfis(this.loadUpdate);
    }

    if (filtroMotivadores) {
      await this.updatePerfisMotivadores(filtroMotivadores);
    } else { 
      this.reloadPerfisMoti = false; 
      this.loadUpdate.push(3);
      this.setUpdatePerfis(this.loadUpdate);
    }

    if (!filtroAxiologia && !filtroComportamento && !filtroMotivadores) {
      this.alertService.showAlertWarning('Nenhum perfil foi selecionado!');
    }

  }

  async setUpdatePerfis(response: Array<number>) {

    this.checkResponse = [...new Set(response)];
    
    if (this.checkResponse?.length === 3) {  
      
      this.loading = false;

      if (this.errorPerfisAxio || this.errorPerfisCompo || this.errorPerfisMoti) {
        this.alertService.showAlertDanger('Erro ao cadastrar Perfis. Por favor, verifique os filtros e tente novamente.');
      } 

      if (!this.reloadPerfisAxio && !this.reloadPerfisCompo && !this.reloadPerfisMoti) {
        if (!this.saveUpdate) {
          this.alertService.showAlertWarning('Não houve alteração nos Perfis Comportamentais!');
        } else {
          this.goBack();
        }
      } 

      if (this.reloadPerfisAxio || this.reloadPerfisCompo || this.reloadPerfisMoti) {
        if (!this.saveUpdate) {
          this.alertService.showAlertSuccess('Perfis cadastrados com sucesso!');
        } else {
          this.goBack();
        }
        this.goTop();
      }

      return;

    }


  }

  customComparator(itemA, itemB) { // Adicionado para customizar a função 'comparator' do OrderBy
    const hasNumber = /\d/;
    const verificaTipo = hasNumber.test(itemA); // Verifica se a variável contém número
    if (verificaTipo === true) {
          return  itemA - itemB; // Corrige a situação que retorna algo assim: [1, 10, 12, 15, 5, 8, 9]
        } else {
         return itemA.localeCompare(itemB); // Corrige a situação da palavra acentuada ficar por último na lista
        }
  }

  goBack(): void {
    this.router.navigateByUrl('/SkipPage', {skipLocationChange: true}).then(()=>
    this.router.navigate(['cargos/' + this.idCargo]));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private goTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

}


*/